import { useEffect, useLayoutEffect, useRef } from 'react';

function write(storageKey: string, value: number): void {
  try {
    window.sessionStorage.setItem(storageKey, String(value));
  } catch {
    // Private mode and full quotas both land here. Scrolling still works.
  }
}

/**
 * Put a list back where you left it.
 *
 * Opening a movie unmounts the list, so returning to it would otherwise start
 * at the top — which means scrolling back through the whole shelf every time
 * you glance at something. React Router only ships `<ScrollRestoration />` for
 * data routers, and this app uses a plain `<Routes>` tree, so the position is
 * kept per tab under an explicit key.
 *
 * The position is remembered as you scroll, and never read back off `window`
 * once the screen starts going away — see the cleanup below, which is where
 * the first attempt at this got it wrong.
 *
 * The restore runs in a layout effect, before paint, so the list does not
 * visibly jump. Poster tiles carry an `aspect-ratio`, so the grid is already
 * its full height at that point even though no artwork has loaded yet;
 * without that the scroll would clamp against a page that is still short.
 */
export function useScrollRestoration(key: string): void {
  const storageKey = `physical-movies.scroll.${key}`;
  /** The last offset seen while this screen was really on screen. */
  const latest = useRef(0);
  const frame = useRef(0);
  const live = useRef(false);

  useLayoutEffect(() => {
    let saved = 0;
    try {
      saved = Number(window.sessionStorage.getItem(storageKey));
      if (!Number.isFinite(saved) || saved < 0) saved = 0;
    } catch {
      // A browser that refuses session storage simply starts at the top.
    }

    if (saved > 0) window.scrollTo(0, saved);
    // Seed it, so leaving without scrolling keeps the restored position
    // instead of writing the 0 we never moved away from.
    latest.current = saved;
    live.current = true;

    return () => {
      // Save here rather than in the passive cleanup below, and this is the
      // whole trick: a layout cleanup runs while this screen is being taken
      // down, before the incoming screen's layout effects. The passive
      // cleanup runs after them — after the movie page has already called
      // `scrollTo(0, 0)` and after the browser has clamped the scroll to a
      // shorter page. Either one fires a scroll event that the listener would
      // faithfully record as "the user is at the top", and the position is
      // gone. Freezing first, and writing what was last seen, is what makes
      // coming back work at all.
      live.current = false;
      write(storageKey, latest.current);
    };
  }, [storageKey]);

  useEffect(() => {
    const onScroll = () => {
      // Anything after the freeze belongs to the next screen, not this one.
      if (!live.current) return;
      latest.current = window.scrollY;
      // Coalesce to one write a frame: a scroll fires per frame on a phone and
      // session storage is synchronous.
      if (frame.current !== 0) return;
      frame.current = window.requestAnimationFrame(() => {
        frame.current = 0;
        if (live.current) write(storageKey, latest.current);
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame.current !== 0) {
        window.cancelAnimationFrame(frame.current);
        frame.current = 0;
      }
    };
  }, [storageKey]);
}

/**
 * Open at the top.
 *
 * The counterpart to the above: nothing else resets the scroll, so tapping a
 * poster from halfway down the shelf, or the bottom bar while scrolled, would
 * otherwise open the next screen already scrolled down.
 */
export function useScrollToTop(): void {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
}
